import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, Trash2, ShoppingBag, ArrowRight, Gift, Zap } from "lucide-react";
import { fetchLoyalty, fetchSavedStacks, deleteSavedStack, money } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Reveal } from "../components/Reveal";
import { toast } from "sonner";

export default function Rewards() {
  const { addItem, setOpen } = useCart();
  const [loyalty, setLoyalty] = useState(null);
  const [saved, setSaved] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchLoyalty().then(setLoyalty).catch(() => {});
    fetchSavedStacks().then(setSaved).catch(() => {});
  }, []);

  const reorder = (s) => {
    s.items.forEach((it) =>
      addItem({ handle: it.handle, name: it.name, dose: it.dose, price: it.price, images: [it.image] }, it.quantity)
    );
    toast.success(`${s.name} added to cart`);
    setOpen(true);
  };

  const removeSaved = async (id) => {
    await deleteSavedStack(id);
    fetchSavedStacks().then(setSaved).catch(() => {});
  };

  const points = loyalty?.points || 0;
  const nxt = loyalty?.next_tier;
  const tiers = loyalty?.tiers || [];
  const currentMin =
    [...tiers].reverse().find((t) => points >= t.min_points)?.min_points ?? 0;
  const progress = nxt
    ? Math.min(100, ((points - currentMin) / (nxt.min_points - currentMin)) * 100)
    : 100;

  return (
    <div className="pt-24 md:pt-28 pb-24">
      {/* header */}
      <div className="border-b border-[#27272A] bg-ink-800/30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-20">
          <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-accent flex items-center gap-2">
            <Award size={14} /> / Athlete Rewards
          </span>
          <h1 className="font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl mt-4 leading-none">
            EARN AS YOU TRAIN
          </h1>
          <p className="text-zinc-400 mt-4 max-w-xl leading-relaxed">
            Every order earns points automatically — no sign-up needed. Climb the ranks and unlock a
            standing discount that stacks on top of your bundle savings.
          </p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 grid lg:grid-cols-12 gap-10">
        {/* status card */}
        <Reveal className="lg:col-span-5">
          <div className="bg-ink-800 border border-[#27272A] p-7 clip-corner-lg" data-testid="loyalty-card">
            <div className="flex items-center justify-between">
              <div>
                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500">
                  Current rank
                </span>
                <p className="font-display font-black text-4xl tracking-tight leading-none mt-1" data-testid="loyalty-tier">
                  {loyalty?.tier || "Rookie"}
                </p>
              </div>
              <div className="h-14 w-14 flex items-center justify-center bg-accent/10 border border-accent/40">
                <Award size={26} className="text-accent" />
              </div>
            </div>

            <div className="mt-6">
              <div className="flex items-baseline justify-between">
                <span className="font-display font-black text-3xl" data-testid="loyalty-points">
                  {points.toLocaleString()}
                </span>
                <span className="font-mono-tech text-[11px] uppercase tracking-widest text-zinc-500">
                  points
                </span>
              </div>
              <div className="mt-3 h-1.5 bg-ink-700 overflow-hidden">
                <div className="h-full bg-accent transition-all duration-700" style={{ width: `${progress}%` }} />
              </div>
              <p className="font-mono-tech text-[11px] text-zinc-500 mt-2">
                {nxt
                  ? `${nxt.points_to_go.toLocaleString()} points to ${nxt.name} (${nxt.percent}% off)`
                  : "Top rank reached — maximum member discount unlocked."}
              </p>
            </div>

            <div className="mt-6 flex items-center gap-2 border border-accent/40 bg-accent/5 px-4 py-3 clip-corner">
              <Zap size={15} className="text-accent" />
              <span className="font-mono-tech text-[11px] uppercase tracking-widest text-zinc-300">
                {loyalty?.tier_percent ? `${loyalty.tier_percent}% member discount active` : "No member discount yet"}
              </span>
            </div>
          </div>
        </Reveal>

        {/* tier ladder */}
        <Reveal className="lg:col-span-7" delay={0.1}>
          <h3 className="font-mono-tech text-xs tracking-[0.3em] uppercase text-zinc-500 mb-5">
            Rank ladder
          </h3>
          <div className="space-y-3">
            {tiers.map((t) => {
              const reached = points >= t.min_points;
              const active = loyalty?.tier === t.name;
              return (
                <div
                  key={t.name}
                  className={`flex items-center justify-between p-4 border transition-colors ${
                    active ? "border-accent bg-accent/5" : "border-[#27272A] bg-ink-800"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <span className={`h-9 w-9 flex items-center justify-center border ${reached ? "border-accent text-accent" : "border-[#27272A] text-zinc-600"}`}>
                      <Award size={16} />
                    </span>
                    <div>
                      <p className="font-display font-bold text-lg leading-none">{t.name}</p>
                      <p className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                        {t.min_points.toLocaleString()}+ points
                      </p>
                    </div>
                  </div>
                  <span className={`font-display font-black text-2xl ${reached ? "text-accent" : "text-zinc-600"}`}>
                    {t.percent}%
                  </span>
                </div>
              );
            })}
          </div>
        </Reveal>
      </div>

      {/* saved stacks */}
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-6">
        <Reveal className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-accent flex items-center gap-2">
              <Gift size={14} /> / Your saved stacks
            </span>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-none">
              REORDER IN ONE TAP
            </h2>
          </div>
          <Link
            to="/build"
            className="flex items-center gap-2 font-mono-tech text-xs uppercase tracking-widest text-zinc-300 hover:text-accent transition-colors"
          >
            Build a new stack <ArrowRight size={14} />
          </Link>
        </Reveal>

        {saved.length === 0 ? (
          <div className="border border-dashed border-[#27272A] p-12 text-center">
            <p className="font-display text-2xl text-zinc-500">No saved stacks yet</p>
            <p className="font-mono-tech text-xs tracking-widest text-zinc-600 uppercase mt-2">
              Build a stack and hit save to reorder it anytime
            </p>
            <Link
              to="/build"
              className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-display font-bold uppercase tracking-wide px-6 py-3 mt-6 transition-colors clip-corner"
            >
              Build a Stack <ArrowRight size={16} />
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rewards-saved-stacks">
            {saved.map((s) => {
              const stotal = s.items.reduce((a, it) => a + it.price * it.quantity, 0);
              return (
                <div key={s.id} className="bg-ink-800 border border-[#27272A] p-5 flex flex-col">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-display font-extrabold text-xl tracking-tight leading-none">{s.name}</h3>
                    <button onClick={() => removeSaved(s.id)} className="text-zinc-600 hover:text-accent transition-colors">
                      <Trash2 size={15} />
                    </button>
                  </div>
                  <div className="flex -space-x-2 mt-4">
                    {s.items.map((it) => (
                      <img key={it.handle} src={it.image} alt={it.name} title={`${it.name} × ${it.quantity}`}
                        className="h-11 w-11 object-cover bg-ink-700 border border-[#27272A]" />
                    ))}
                  </div>
                  <p className="font-mono-tech text-[11px] text-zinc-500 mt-3">
                    {s.items.length} item{s.items.length === 1 ? "" : "s"} · {money(stotal)}
                  </p>
                  <button
                    onClick={() => reorder(s)}
                    className="mt-4 flex items-center justify-center gap-2 bg-white text-ink-900 hover:bg-accent hover:text-white font-display font-bold text-sm uppercase tracking-wide py-2.5 transition-colors"
                  >
                    <ShoppingBag size={15} /> Reorder
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
